import { Link } from 'react-router-dom'
import { MapPin, Phone, Mail, Facebook, Linkedin, ArrowRight, Clock, Shield, Truck, CreditCard, MessageCircle } from 'lucide-react'
import { EMAIL, TELEPHONES, lienTel, lienWhatsApp } from '../data/contact'
import { useCategories } from '../hooks/useCategories'
import './Footer.css'

const STORES_SUMMARY = [
  { city: 'Conakry', count: 7, zones: ['Hamdallaye', 'Lambandji', 'Kobayah', 'Samataran', 'Gomboyah', 'Sanoyah', 'Sonfonia T8'] },
  { city: 'Labé',    count: 1, zones: ['Quartier Djolou'] },
  { city: 'Kankan',  count: 1, zones: ['Quartier Kabada 2'] },
]

/*
 | Chaque lien mene a une page ou a une section qui existe.
 |
 | Les dix-sept liens de ces colonnes pointaient tous vers « # » : un clic
 | ramenait en haut de la page, sans rien ouvrir. Certains annoncaient des
 | pages qui n'ont jamais ete ecrites (recrutement, blog, partenariats,
 | retours, FAQ). Ils sont retires ; ils reviendront avec leur contenu.
 |
 | La colonne Catalogue est lue dans la base, comme les villes de livraison :
 | elle oubliait « Motos electriques » et inventait un rayon « Rideaux & Deco »
 | qui n'existe pas sous ce nom.
 */
const LIENS_MAISON = [
  { fr: 'À propos de nous', en: 'About us',   to: '/#about'  },
  { fr: 'Nos magasins',     en: 'Our stores', to: '/#stores' },
]

const LIENS_AIDE = [
  { fr: 'Comment commander',   en: 'How to order',      to: '/#comment-commander' },
  { fr: 'Moyens de paiement',  en: 'Payment methods',   to: '/cgu#fonctionnement' },
  { fr: 'Suivre une commande', en: 'Track an order',    to: '/suivi' },
  { fr: 'Conditions générales', en: 'Terms of use',     to: '/cgu' },
]

const TRUST_BADGES = [
  { icon: Shield,    fr: 'Paiement sécurisé',  en: 'Secure payment'  },
  { icon: Truck,     fr: 'Livraison Guinée',   en: 'Guinea delivery' },
  { icon: CreditCard,fr: 'Orange Money',       en: 'Orange Money'    },
  { icon: Clock,     fr: 'Lun–Sam 9h–18h',     en: 'Mon–Sat 9–6pm'  },
]

export default function Footer({ lang }) {
  const t = (obj) => (typeof obj === 'string' ? obj : (obj?.[lang] ?? obj?.fr ?? ''))
  const { categories } = useCategories()

  return (
    <footer className="footer">

      {/*
        Offres sur WhatsApp.
        Il y avait ici un formulaire « Newsletter » dont l'envoi etait
        simplement annule : aucune route, aucune table ne recevait l'adresse.
        Le client tapait son e-mail, cliquait « S'inscrire », et rien ne se
        passait, pas meme un message. Les clients lisent WhatsApp bien plus
        que leurs e-mails : le bouton ouvre la conversation, message pret.
      */}
      <div className="footer-newsletter">
        <div className="fn-inner">
          <div className="fn-text">
            <span className="fn-label">WhatsApp</span>
            <h3>{lang === 'fr' ? 'Recevez nos offres sur WhatsApp' : 'Get our offers on WhatsApp'}</h3>
            <p>{lang === 'fr' ? 'Écrivez-nous : nous vous envoyons nos nouveautés et nos promotions.' : 'Message us and we will send you our new arrivals and promotions.'}</p>
          </div>
          <a
            className="fn-btn"
            href={lienWhatsApp(lang === 'fr'
              ? 'Bonjour, je souhaite recevoir vos offres et nouveautés sur WhatsApp.'
              : 'Hello, I would like to receive your offers and new arrivals on WhatsApp.')}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MessageCircle size={16} />
            {lang === 'fr' ? 'Recevoir les offres' : 'Get the offers'}
            <ArrowRight size={15} />
          </a>
        </div>
      </div>

      {/* Trust badges */}
      <div className="footer-trust">
        <div className="ft-inner">
          {TRUST_BADGES.map(({ icon: TrustIcon, fr, en }, i) => (
            <div key={i} className="trust-badge">
              <div className="trust-icon"><TrustIcon size={16} /></div>
              <span>{lang === 'fr' ? fr : en}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main */}
      <div className="footer-main">
        <div className="footer-inner">

          {/* Brand column */}
          <div className="footer-brand">
            <img src="/logo.png" alt="ABBUSINESS" className="footer-logo-img" />
            <p className="footer-tagline">
              {lang === 'fr'
                ? 'Votre destination premium pour le mobilier, l\'électroménager et la décoration en Guinée.'
                : 'Your premium destination for furniture, appliances and décor in Guinea.'}
            </p>

            <div className="footer-contact">
              {TELEPHONES.map((numero) => (
                <a key={numero} href={lienTel(numero)} className="footer-contact-item">
                  <Phone size={13} /><span>{numero}</span>
                </a>
              ))}
              <a href={`mailto:${EMAIL}`} className="footer-contact-item">
                <Mail size={13} /><span>{EMAIL}</span>
              </a>
              <div className="footer-contact-item">
                <Clock size={13} /><span>{lang === 'fr' ? 'Lun–Sam  9h–18h  •  Dim Fermé' : 'Mon–Sat  9AM–6PM  •  Sun Closed'}</span>
              </div>
              <div className="footer-contact-item">
                <MapPin size={13} /><span>Conakry, Labé, Kankan, Guinée</span>
              </div>
            </div>

            <div className="footer-socials">
              <a href="https://www.facebook.com/share/19JTsXAXd1/" target="_blank" rel="noreferrer" className="social-btn" aria-label="Facebook"><Facebook size={16} /></a>
              <a href="https://www.linkedin.com/company/abbusiness-sarl/" target="_blank" rel="noreferrer" className="social-btn" aria-label="LinkedIn"><Linkedin size={16} /></a>
            </div>
          </div>

          {/* Colonnes de liens */}
          <div className="footer-col">
            <h4 className="footer-col-title">{lang === 'fr' ? 'Catalogue' : 'Catalog'}</h4>
            <ul className="footer-col-links">
              <li><Link to="/catalogue">{lang === 'fr' ? 'Tout le catalogue' : 'Full catalog'}</Link></li>
              {categories.map((c) => (
                <li key={c.slug}><Link to={`/catalogue?category=${c.slug}`}>{t(c.name)}</Link></li>
              ))}
            </ul>
          </div>
          {[['ABBUSINESS', LIENS_MAISON], [lang === 'fr' ? 'Aide' : 'Help', LIENS_AIDE]].map(([titre, liens]) => (
            <div key={titre} className="footer-col">
              <h4 className="footer-col-title">{titre}</h4>
              <ul className="footer-col-links">
                {liens.map((l) => <li key={l.to}><Link to={l.to}>{t(l)}</Link></li>)}
              </ul>
            </div>
          ))}

          {/* Stores column */}
          <div className="footer-col">
            <h4 className="footer-col-title">{lang === 'fr' ? 'Nos Magasins' : 'Our Stores'}</h4>
            <div className="footer-stores">
              {STORES_SUMMARY.map((s, i) => (
                <div key={i} className="footer-store">
                  <div className="fs-header">
                    <div className="fs-dot" />
                    <span className="fs-city">{s.city}</span>
                    <span className="fs-count">{s.count}</span>
                  </div>
                  <div className="fs-zones">
                    {s.zones.map((z, j) => <span key={j} className="fs-zone">{z}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* Bottom */}
      <div className="footer-bottom">
        <div className="fb-inner">
          <p>© {new Date().getFullYear()} ABBUSINESS. {lang === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}</p>
          <div className="fb-links">
            <Link to="/confidentialite">{lang === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy'}</Link>
            <Link to="/cgu">{lang === 'fr' ? 'CGU' : 'Terms'}</Link>
          </div>
          <span className="fb-made">{lang === 'fr' ? 'Fait avec ❤ en Guinée' : 'Made with ❤ in Guinea'}</span>
        </div>
      </div>

    </footer>
  )
}
